import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import {
  ArrowTrendingUpIcon,
  ArrowsRightLeftIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

const DAY_OPTIONS = [7, 14, 30];

const statusBadgeClasses = {
  new_lead: 'bg-indigo-100 text-indigo-800',
  unable_to_send_message: 'bg-amber-100 text-amber-800',
  initial_msg_sent: 'bg-blue-100 text-blue-800',
  interviewed: 'bg-yellow-100 text-yellow-800',
  offered: 'bg-purple-100 text-purple-800',
  hired: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  on_hold: 'bg-gray-100 text-gray-800'
};

const formatShortDate = (value) => new Date(value).toLocaleDateString(undefined, {
  month: 'short',
  day: 'numeric'
});

const formatDateTime = (value) => new Date(value).toLocaleString(undefined, {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit'
});

const Reports = () => {
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await axios.get('/api/reports/overview', {
          params: { days }
        });
        setData(response.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load reports');
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [days]);

  const maxInterviews = Math.max(1, ...(data?.interviewTrend || []).map((item) => item.interviews || 0));

  return (
    <Layout>
      <div className="space-y-6 px-4 py-6 sm:px-0">
        <div className="flex flex-col gap-4 rounded-[28px] border border-gray-200 bg-white px-6 py-5 shadow-sm lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-black tracking-tight text-gray-900">Reports</h1>
            <p className="mt-1 text-sm text-gray-500">
              Interview activity, recent outcomes, and lead status movement from your existing CRM history.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {DAY_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setDays(option)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  days === option
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Last {option} days
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="rounded-[28px] border border-gray-200 bg-white px-6 py-12 text-center text-gray-500 shadow-sm">
            Loading reports...
          </div>
        ) : error ? (
          <div className="rounded-[28px] border border-red-200 bg-red-50 px-6 py-12 text-center text-red-700 shadow-sm">
            {error}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="rounded-[24px] border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-500">
                  <ChatBubbleLeftRightIcon className="h-5 w-5 text-yellow-500" />
                  Interviews Logged
                </div>
                <div className="mt-3 text-3xl font-black text-gray-900">{data?.interviewSummary?.total || 0}</div>
                <div className="mt-1 text-sm text-gray-500">
                  {data?.interviewSummary?.avgPerDay || 0} per day over the last {data?.days} days
                </div>
              </div>

              <div className="rounded-[24px] border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-500">
                  <UserGroupIcon className="h-5 w-5 text-blue-500" />
                  Unique Interviewed Leads
                </div>
                <div className="mt-3 text-3xl font-black text-gray-900">{data?.interviewSummary?.uniqueApplicants || 0}</div>
                <div className="mt-1 text-sm text-gray-500">
                  Based on the latest interview transition for each applicant
                </div>
              </div>

              <div className="rounded-[24px] border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-500">
                  <ArrowsRightLeftIcon className="h-5 w-5 text-emerald-500" />
                  Status Changes
                </div>
                <div className="mt-3 text-3xl font-black text-gray-900">{data?.recentStatusChanges?.length || 0}</div>
                <div className="mt-1 text-sm text-gray-500">
                  Most recent tracked lead status updates in this window
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.6fr_1fr]">
              <div className="rounded-[28px] border border-gray-200 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-2">
                  <ArrowTrendingUpIcon className="h-5 w-5 text-blue-500" />
                  <h2 className="text-lg font-bold text-gray-900">Interview Trend</h2>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Daily count of leads moved into the <span className="font-semibold text-gray-700">interviewed</span> status.
                </p>

                <div className="mt-6 grid grid-cols-7 gap-3 md:grid-cols-14 xl:grid-cols-none xl:grid-flow-col xl:auto-cols-fr">
                  {(data?.interviewTrend || []).map((item) => (
                    <div key={item.date} className="flex min-w-0 flex-col items-center">
                      <div className="flex h-40 w-full items-end justify-center rounded-2xl bg-gray-50 px-2 py-3">
                        <div
                          className="w-full rounded-xl bg-blue-500"
                          style={{ height: `${Math.max((item.interviews / maxInterviews) * 100, item.interviews > 0 ? 12 : 0)}%` }}
                        />
                      </div>
                      <div className="mt-2 text-sm font-semibold text-gray-900">{item.interviews}</div>
                      <div className="text-xs text-gray-500">{formatShortDate(item.date)}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-[28px] border border-gray-200 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-2">
                  <ClockIcon className="h-5 w-5 text-violet-500" />
                  <h2 className="text-lg font-bold text-gray-900">Recent Interview Outcomes</h2>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Current status of applicants who were moved to interviewed during this period.
                </p>

                <div className="mt-5 space-y-3">
                  {(data?.interviewOutcomes || []).length > 0 ? (
                    data.interviewOutcomes.map((outcome) => (
                      <div key={outcome.status} className="flex items-center justify-between rounded-2xl bg-gray-50 px-4 py-3">
                        <span className={`rounded-full px-3 py-1 text-xs font-bold ${statusBadgeClasses[outcome.status] || 'bg-gray-100 text-gray-700'}`}>
                          {outcome.label}
                        </span>
                        <span className="text-lg font-black text-gray-900">{outcome.count}</span>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-2xl bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                      No interview transitions recorded in this time range.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <div className="rounded-[28px] border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-bold text-gray-900">Top Status Transitions</h2>
                <p className="mt-1 text-sm text-gray-500">
                  The most common lead status movements recorded during the selected period.
                </p>

                <div className="mt-5 space-y-3">
                  {(data?.topTransitions || []).length > 0 ? (
                    data.topTransitions.map((transition) => (
                      <div key={`${transition.from}-${transition.to}`} className="flex items-center justify-between rounded-2xl border border-gray-100 px-4 py-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-gray-900">
                            {transition.fromLabel} to {transition.toLabel}
                          </div>
                        </div>
                        <div className="ml-4 text-lg font-black text-gray-900">{transition.count}</div>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-2xl bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                      No status transitions found.
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-[28px] border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-bold text-gray-900">Recent Interviewed Leads</h2>
                <p className="mt-1 text-sm text-gray-500">
                  Latest interview entries, paired with each lead&apos;s current status.
                </p>

                <div className="mt-5 space-y-3">
                  {(data?.recentInterviews || []).length > 0 ? (
                    data.recentInterviews.map((item) => (
                      <div key={item.id} className="rounded-2xl border border-gray-100 px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-bold text-gray-900">{item.applicantName}</div>
                            <div className="mt-1 text-xs text-gray-500">
                              {item.position || 'No position'}{item.leadSource ? ` • ${item.leadSource}` : ''}
                            </div>
                          </div>
                          <span className={`rounded-full px-3 py-1 text-xs font-bold ${statusBadgeClasses[item.currentStatus] || 'bg-gray-100 text-gray-700'}`}>
                            {item.currentStatusLabel}
                          </span>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                          Interview logged {formatDateTime(item.interviewedAt)} by {item.changedBy}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-2xl bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                      No recent interview entries found.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-[28px] border border-gray-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900">Recent Lead Status Changes</h2>
              <p className="mt-1 text-sm text-gray-500">
                Audit-style view of how lead statuses changed during the selected period.
              </p>

              {(data?.recentStatusChanges || []).length > 0 ? (
                <div className="mt-5 overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Lead</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Status Change</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Changed By</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">When</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {data.recentStatusChanges.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="text-sm font-semibold text-gray-900">{item.applicantName}</div>
                            <div className="text-xs text-gray-500">
                              {item.position || 'No position'}{item.leadSource ? ` • ${item.leadSource}` : ''}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-gray-900">{item.fromLabel} to {item.toLabel}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{item.changedBy}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{formatDateTime(item.changedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="mt-5 rounded-2xl bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                  No recent status changes found.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Reports;
